import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Check, LogOut, Plus, Trash2, PartyPopper, ArrowRight, Lock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import {
  emptyProfile, STEPS, fmtINR, summarize,
  DEFAULT_EXPENSE_TEMPLATES, EXPENSE_GROUPS, LOAN_TYPES, GOAL_PRESETS,
} from "@/lib/financial";
import Logo from "@/components/Logo";

const inputCls = "h-11 rounded-xl border-[#E5E2DA] bg-white focus-visible:ring-[#1A3626]/20 focus-visible:border-[#1A3626] focus-visible:ring-2 text-[#1C1D1C]";

function FNum({ id, label, value, onChange, prefix = "₹", placeholder = "0", suffix }) {
  return (
    <div>
      {label && <Label htmlFor={id} className="block text-sm font-medium text-[#1C1D1C] mb-2">{label}</Label>}
      <div className="relative">
        {prefix && <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A8D8A] mono text-sm">{prefix}</span>}
        <Input id={id} data-testid={`field-${id}`} type="number" inputMode="decimal" min="0"
          placeholder={placeholder} value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputCls} ${prefix ? "pl-8" : ""} ${suffix ? "pr-12" : ""}`}/>
        {suffix && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8A8D8A] text-xs">{suffix}</span>}
      </div>
    </div>
  );
}
function FText({ id, label, value, onChange, type = "text", placeholder = "" }) {
  return (
    <div>
      {label && <Label htmlFor={id} className="block text-sm font-medium text-[#1C1D1C] mb-2">{label}</Label>}
      <Input id={id} data-testid={`field-${id}`} type={type} placeholder={placeholder}
        value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls}/>
    </div>
  );
}
function SectionLabel({ children }) {
  return <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A] mb-3">{children}</div>;
}

export default function IntakeForm() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(emptyProfile);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [locked, setLocked] = useState(false);
  const [reopening, setReopening] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile");
        const merged = { ...emptyProfile, ...data,
          family: { ...emptyProfile.family, ...(data.family || {}), spouse: { ...emptyProfile.family.spouse, ...((data.family||{}).spouse||{}) } },
          retirement: { ...emptyProfile.retirement, ...(data.retirement || {}) },
          income: { ...emptyProfile.income, ...(data.income || {}) },
          financial_assets: { ...emptyProfile.financial_assets, ...(data.financial_assets || {}) },
          fixed_assets: { ...emptyProfile.fixed_assets, ...(data.fixed_assets || {}) },
          investments_monthly: { ...emptyProfile.investments_monthly, ...(data.investments_monthly || {}) },
          investments_yearly: { ...emptyProfile.investments_yearly, ...(data.investments_yearly || {}) },
          insurance: { ...emptyProfile.insurance, ...(data.insurance || {}) },
          expenses: { ...emptyProfile.expenses, ...(data.expenses || {}) },
          personal: { ...emptyProfile.personal, ...(data.personal || {}) },
        };
        setProfile(merged);
        // Lock the form if previously submitted and not explicitly reopened.
        setLocked(Boolean(data.submitted) && !data.editing_open);
      } catch (err) {
        console.error("profile load failed", err);
      }
      setLoading(false);
    })();
  }, []);

  const reopenForUpdate = async () => {
    setReopening(true);
    try {
      await api.post("/profile/reopen");
      setLocked(false);
      toast.success("Intake unlocked — update what's changed");
    } catch (err) {
      console.error("reopen failed", err);
      toast.error("Couldn't unlock — please retry");
    } finally {
      setReopening(false);
    }
  };

  const setSection = (key, partial) => setProfile((p) => ({ ...p, [key]: { ...p[key], ...partial } }));
  const setNested = (a, b, c) => setProfile((p) => ({ ...p, [a]: { ...p[a], [b]: { ...(p[a]?.[b] || {}), ...c } } }));

  const save = async ({ submit = false } = {}) => {
    setSaving(true);
    try {
      const payload = { ...profile, submitted: submit || profile.submitted };
      const { data } = await api.put("/profile", payload);
      setProfile((p) => ({ ...p, ...data }));
      if (submit) { setJustSubmitted(true); toast.success("Submitted to your advisor"); }
      else toast.success("Progress saved");
    } catch {
      toast.error("Couldn't save — please try again");
    } finally { setSaving(false); }
  };

  const progress = useMemo(() => Math.round(((step + 1) / STEPS.length) * 100), [step]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#5B5E5B]">Loading…</div>;

  if (justSubmitted) return <SubmissionSuccess user={user} navigate={navigate} logout={logout}/>;

  if (locked) return <LockedView user={user} profile={profile} navigate={navigate} logout={logout} onUnlock={reopenForUpdate} reopening={reopening}/>;

  // ============ Step Bodies ============
  const stepBody = () => {
    switch (STEPS[step].key) {
      case "family": return <StepFamily profile={profile} setSection={setSection} setNested={setNested} setProfile={setProfile}/>;
      case "income": return <StepIncome profile={profile} setSection={setSection}/>;
      case "expenses": return <StepExpenses profile={profile} setProfile={setProfile}/>;
      case "assets": return <StepAssets profile={profile} setSection={setSection}/>;
      case "liabilities": return <StepLoans profile={profile} setProfile={setProfile}/>;
      case "investments": return <StepInvestments profile={profile} setSection={setSection}/>;
      case "insurance": return <StepInsurance profile={profile} setSection={setSection}/>;
      case "goals": return <StepGoals profile={profile} setProfile={setProfile}/>;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen page-tinted-soft relative">
      <header className="border-b border-[#E5E2DA] bg-white/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between py-4">
          <Logo/>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#5B5E5B] hidden sm:inline">{user?.name}</span>
            <Button data-testid="back-to-dashboard" variant="ghost" onClick={() => navigate("/dashboard")} className="rounded-full">Dashboard</Button>
            <Button data-testid="logout-btn" variant="ghost" onClick={async () => { await logout(); navigate("/"); }} className="rounded-full">
              <LogOut className="h-4 w-4 mr-2"/> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-12 py-12 relative">
        <div className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Step {step + 1} of {STEPS.length} · {STEPS[step].label}</span>
            <span className="text-xs mono text-[#8A8D8A]">{progress}%</span>
          </div>
          <div className="h-1.5 bg-[#F4F2EC] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#1A3626] via-[#3A5C45] to-[#C78D4A] transition-all duration-500" style={{ width: `${progress}%` }}/>
          </div>
          <div className="hidden md:flex flex-wrap gap-x-4 gap-y-1 mt-4">
            {STEPS.map((s, i) => (
              <button key={s.key} data-testid={`stepper-${s.key}`} onClick={() => setStep(i)}
                className={`text-sm transition-colors ${i === step ? "text-[#1A3626] font-medium" : i < step ? "text-[#5B5E5B]" : "text-[#8A8D8A]"}`}>
                {i < step && <Check className="inline h-3 w-3 mr-1"/>}{s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-[#E5E2DA] bg-white p-8 md:p-10 fade-up" key={step}>
          {stepBody()}
        </div>

        <div className="flex items-center justify-between mt-8">
          <Button data-testid="step-prev" variant="ghost" disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))} className="rounded-full">
            <ChevronLeft className="h-4 w-4 mr-1"/> Back
          </Button>
          <div className="flex items-center gap-3">
            <Button data-testid="step-save" variant="outline" onClick={() => save()} disabled={saving} className="rounded-full border-[#E5E2DA]">
              {saving ? "Saving…" : "Save draft"}
            </Button>
            {step < STEPS.length - 1 ? (
              <Button data-testid="step-next" onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                className="rounded-full bg-[#1A3626] hover:bg-[#2C4C3B] text-white">
                Next <ChevronRight className="h-4 w-4 ml-1"/>
              </Button>
            ) : (
              <Button data-testid="step-submit" onClick={() => save({ submit: true })} disabled={saving}
                className="rounded-full bg-[#1A3626] hover:bg-[#2C4C3B] text-white">
                {saving ? "Submitting…" : "Submit to advisor"}
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// ================== STEPS ==================
function StepFamily({ profile, setSection, setNested, setProfile }) {
  const addChild = () => setProfile((p) => ({ ...p, family: { ...p.family, children: [...(p.family.children||[]), { _id: `c-${Date.now()}-${Math.random().toString(36).slice(2,7)}`, name: "", age: "" }] } }));
  const updChild = (i, k, v) => setProfile((p) => {
    const arr = [...(p.family.children||[])]; arr[i] = { ...arr[i], [k]: v };
    return { ...p, family: { ...p.family, children: arr } };
  });
  const rmChild = (i) => setProfile((p) => {
    const arr = [...(p.family.children||[])]; arr.splice(i, 1);
    return { ...p, family: { ...p.family, children: arr } };
  });

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-heading text-2xl sm:text-3xl font-medium">Family & planning</h2>
        <p className="text-[#5B5E5B] mt-2">A bit about you, your spouse and children — and the year you&apos;re planning for.</p>
      </div>

      <div className="space-y-6">
        <SectionLabel>Year of planning</SectionLabel>
        <FNum id="year_of_planning" label="" value={profile.personal.year_of_planning} prefix="" placeholder="2026" onChange={(v) => setSection("personal", { year_of_planning: v })}/>
      </div>

      <div className="space-y-6">
        <SectionLabel>About you</SectionLabel>
        <FText id="full_name" label="Full name" value={profile.personal.full_name} onChange={(v) => setSection("personal", { full_name: v })}/>
        <div className="grid sm:grid-cols-2 gap-5">
          <FText id="phone" label="Phone" value={profile.personal.phone} onChange={(v) => setSection("personal", { phone: v })} placeholder="+91 …"/>
          <FText id="age" label="Age" type="number" value={profile.personal.age} onChange={(v) => setSection("personal", { age: v })}/>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <FText id="occupation" label="Occupation" value={profile.personal.occupation} onChange={(v) => setSection("personal", { occupation: v })}/>
          <FText id="city" label="City" value={profile.personal.city} onChange={(v) => setSection("personal", { city: v })}/>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="block text-sm font-medium text-[#1C1D1C] mb-2">Marital status</Label>
            <Select value={profile.personal.marital_status || ""} onValueChange={(v) => setSection("personal", { marital_status: v })}>
              <SelectTrigger data-testid="field-marital_status" className={inputCls}><SelectValue placeholder="Select"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="single">Single</SelectItem>
                <SelectItem value="married">Married</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <FText id="dependents" label="Dependents" type="number" value={profile.personal.dependents} onChange={(v) => setSection("personal", { dependents: v })}/>
        </div>
      </div>

      <div className="space-y-6">
        <SectionLabel>Spouse</SectionLabel>
        <div className="grid sm:grid-cols-2 gap-5">
          <FText id="spouse_name" label="Name" value={profile.family.spouse.name} onChange={(v) => setNested("family", "spouse", { name: v })}/>
          <FText id="spouse_age" label="Age" type="number" value={profile.family.spouse.age} onChange={(v) => setNested("family", "spouse", { age: v })}/>
        </div>
        <FText id="spouse_occupation" label="Occupation" value={profile.family.spouse.occupation} onChange={(v) => setNested("family", "spouse", { occupation: v })}/>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <SectionLabel>Children</SectionLabel>
          <Button type="button" data-testid="add-child" variant="ghost" onClick={addChild} className="rounded-full text-[#1A3626]">
            <Plus className="h-4 w-4 mr-1"/> Add child
          </Button>
        </div>
        {(profile.family.children || []).length === 0 && <p className="text-sm text-[#8A8D8A]">No children added.</p>}
        {(profile.family.children || []).map((c, i) => (
          <div key={c._id || `child-${i}`} className="rounded-xl border border-[#E5E2DA] p-4 bg-[#FDFBF7] space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Child {i + 1}</div>
              <Button type="button" variant="ghost" data-testid={`rm-child-${i}`} onClick={() => rmChild(i)} className="text-[#9E473D] hover:text-[#9E473D]">
                <Trash2 className="h-4 w-4"/>
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <FText id={`child_name_${i}`} label="Name" value={c.name} onChange={(v) => updChild(i, "name", v)}/>
              <FText id={`child_age_${i}`} label="Age" type="number" value={c.age} onChange={(v) => updChild(i, "age", v)}/>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-6">
        <SectionLabel>Retirement</SectionLabel>
        <div className="grid sm:grid-cols-2 gap-5">
          <FText id="current_age" label="Your current age" type="number" value={profile.retirement.current_age} onChange={(v) => setSection("retirement", { current_age: v })}/>
          <FText id="retirement_age" label="Desired retirement age" type="number" value={profile.retirement.retirement_age} onChange={(v) => setSection("retirement", { retirement_age: v })}/>
          <FText id="retirement_year" label="Retirement year" type="number" value={profile.retirement.retirement_year} onChange={(v) => setSection("retirement", { retirement_year: v })}/>
          <FText id="life_expectancy" label="Life expectancy (years)" type="number" value={profile.retirement.life_expectancy} onChange={(v) => setSection("retirement", { life_expectancy: v })}/>
          <FNum id="post_retirement_income" label="Expected monthly income after retirement" value={profile.retirement.post_retirement_income} onChange={(v) => setSection("retirement", { post_retirement_income: v })}/>
          <FNum id="monthly_expense_today" label="Monthly expense needed today" value={profile.retirement.monthly_expense_today} onChange={(v) => setSection("retirement", { monthly_expense_today: v })}/>
        </div>
      </div>
    </div>
  );
}

function StepIncome({ profile, setSection }) {
  const inc = profile.income;
  const set = (k, v) => setSection("income", { [k]: v });
  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-heading text-2xl sm:text-3xl font-medium">Income</h2>
        <p className="text-[#5B5E5B] mt-2">All figures post-tax. Leave blank what doesn&apos;t apply.</p>
      </div>

      <div>
        <SectionLabel>Monthly inflow (post-tax)</SectionLabel>
        <div className="grid sm:grid-cols-2 gap-5">
          <FNum id="salary_self" label="Your take-home salary" value={inc.salary_self} onChange={(v) => set("salary_self", v)}/>
          <FNum id="pf_self" label="Your PF deducted" value={inc.pf_self} onChange={(v) => set("pf_self", v)}/>
          <FNum id="salary_spouse" label="Spouse take-home salary" value={inc.salary_spouse} onChange={(v) => set("salary_spouse", v)}/>
          <FNum id="pf_spouse" label="Spouse PF deducted" value={inc.pf_spouse} onChange={(v) => set("pf_spouse", v)}/>
          <FNum id="business_income" label="Business income" value={inc.business_income} onChange={(v) => set("business_income", v)}/>
          <FNum id="rent_received" label="Rent received" value={inc.rent_received} onChange={(v) => set("rent_received", v)}/>
          <FNum id="interest_received" label="Interest received" value={inc.interest_received} onChange={(v) => set("interest_received", v)}/>
          <FNum id="dividend_received" label="Dividend received" value={inc.dividend_received} onChange={(v) => set("dividend_received", v)}/>
          <FNum id="other_income" label="Other monthly income" value={inc.other_income} onChange={(v) => set("other_income", v)}/>
        </div>
      </div>

      <div>
        <SectionLabel>Yearly inflow</SectionLabel>
        <div className="grid sm:grid-cols-2 gap-5">
          <FNum id="bonus_self" label="Your annual bonus" value={inc.bonus_self} onChange={(v) => set("bonus_self", v)}/>
          <FNum id="bonus_spouse" label="Spouse annual bonus" value={inc.bonus_spouse} onChange={(v) => set("bonus_spouse", v)}/>
          <FNum id="business_yearly" label="Business yearly income" value={inc.business_yearly} onChange={(v) => set("business_yearly", v)}/>
          <FNum id="other_yearly" label="Other yearly income" value={inc.other_yearly} onChange={(v) => set("other_yearly", v)}/>
        </div>
      </div>
    </div>
  );
}

function StepExpenses({ profile, setProfile }) {
  const addRow = (group, name = "", frequency = "Monthly") => setProfile((p) => {
    const cur = p.expenses?.[group] || [];
    return { ...p, expenses: { ...p.expenses, [group]: [...cur, { _id: `e-${Date.now()}-${Math.random().toString(36).slice(2,7)}`, name, amount: "", frequency }] } };
  });
  const updRow = (group, i, k, v) => setProfile((p) => {
    const arr = [...(p.expenses?.[group] || [])]; arr[i] = { ...arr[i], [k]: v };
    return { ...p, expenses: { ...p.expenses, [group]: arr } };
  });
  const rmRow = (group, i) => setProfile((p) => {
    const arr = [...(p.expenses?.[group] || [])]; arr.splice(i, 1);
    return { ...p, expenses: { ...p.expenses, [group]: arr } };
  });
  const seedTemplate = (group) => {
    const items = DEFAULT_EXPENSE_TEMPLATES[group] || [];
    setProfile((p) => ({ ...p, expenses: { ...p.expenses, [group]: items.map((n, idx) => ({ _id: `e-${Date.now()}-${idx}-${Math.random().toString(36).slice(2,5)}`, name: n, amount: "", frequency: "Monthly" })) } }));
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-heading text-2xl sm:text-3xl font-medium">Monthly & annual expenses</h2>
        <p className="text-[#5B5E5B] mt-2">Add rows by category. Mark each as Monthly or Annually. Tap &quot;Use template&quot; to start with common entries.</p>
      </div>

      {EXPENSE_GROUPS.map(({ key, label }) => (
        <div key={key} className="space-y-3">
          <div className="flex items-center justify-between">
            <SectionLabel>{label}</SectionLabel>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" data-testid={`template-${key}`} onClick={() => seedTemplate(key)} className="rounded-full text-[#1A3626] text-xs">Use template</Button>
              <Button type="button" variant="ghost" data-testid={`add-expense-${key}`} onClick={() => addRow(key)} className="rounded-full text-[#1A3626]"><Plus className="h-4 w-4 mr-1"/>Add</Button>
            </div>
          </div>
          {(profile.expenses?.[key] || []).length === 0 && <p className="text-sm text-[#8A8D8A]">No items yet.</p>}
          <div className="space-y-2">
            {(profile.expenses?.[key] || []).map((row, i) => (
              <div key={row._id || `row-${i}`} className="grid grid-cols-1 sm:grid-cols-[1fr_140px_120px_40px] gap-2 items-end">
                <Input data-testid={`exp-${key}-name-${i}`} placeholder="Item name" value={row.name} onChange={(e) => updRow(key, i, "name", e.target.value)} className={inputCls}/>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8D8A] mono text-sm">₹</span>
                  <Input data-testid={`exp-${key}-amt-${i}`} type="number" inputMode="decimal" min="0" placeholder="0"
                    value={row.amount} onChange={(e) => updRow(key, i, "amount", e.target.value)}
                    className={`${inputCls} pl-7`}/>
                </div>
                <Select value={row.frequency || "Monthly"} onValueChange={(v) => updRow(key, i, "frequency", v)}>
                  <SelectTrigger data-testid={`exp-${key}-freq-${i}`} className={inputCls}><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Monthly">Monthly</SelectItem>
                    <SelectItem value="Annually">Annually</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="button" variant="ghost" data-testid={`rm-exp-${key}-${i}`} onClick={() => rmRow(key, i)} className="text-[#9E473D] hover:text-[#9E473D]">
                  <Trash2 className="h-4 w-4"/>
                </Button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function StepAssets({ profile, setSection }) {
  const fa = profile.financial_assets, fx = profile.fixed_assets;
  const setFA = (k, v) => setSection("financial_assets", { [k]: v });
  const setFX = (k, v) => setSection("fixed_assets", { [k]: v });
  const FAs = [
    ["savings_bank", "Savings / Bank balance"], ["current_account", "Current account"],
    ["fixed_deposits", "Fixed deposits"], ["epf_self", "EPF (Self)"], ["epf_spouse", "EPF (Spouse)"],
    ["ppf", "PPF"], ["nps", "NPS"], ["direct_equity", "Direct equity"], ["esops_rsus", "ESOPs / ESPPs / RSUs"],
    ["mutual_funds_equity", "Mutual funds (Equity)"], ["mutual_funds_debt", "Mutual funds (Debt)"],
    ["bonds_debentures", "Bonds / debentures"], ["pomis_nsc_kvp", "POMIS / NSC / KVP"],
    ["sovereign_gold_bonds", "Sovereign Gold Bonds"], ["gold_coins_bars", "Gold (coins / bars)"],
    ["silver", "Silver"], ["rent_deposits_paid", "Rent deposits paid"],
    ["crypto", "Crypto / Bitcoins"], ["sukanya_samriddhi", "Sukanya Samriddhi"],
    ["insurance_policies_cv", "Insurance policy cash value"], ["gratuity", "Gratuity (accumulated)"],
    ["other_financial", "Other financial assets"],
  ];
  const FXs = [
    ["own_house", "Own house (lifestyle)"], ["vacation_house", "Vacation house"],
    ["vehicles", "Vehicles"], ["gold_jewellery", "Gold jewellery"],
    ["recreational_equipment", "Recreational equipment"], ["household_furnishings", "Household furnishings"],
    ["collectibles", "Collectibles (art, stamps, coins)"],
    ["investment_real_estate", "Investment real estate"], ["land", "Land"],
    ["other_fixed", "Other fixed assets"],
  ];
  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-heading text-2xl sm:text-3xl font-medium">Assets</h2>
        <p className="text-[#5B5E5B] mt-2">Approximate current value. Fill what&apos;s relevant.</p>
      </div>
      <div>
        <SectionLabel>Financial assets</SectionLabel>
        <div className="grid sm:grid-cols-2 gap-5">
          {FAs.map(([k, l]) => <FNum key={k} id={k} label={l} value={fa[k]} onChange={(v) => setFA(k, v)}/>)}
        </div>
      </div>
      <div>
        <SectionLabel>Fixed assets (lifestyle + investments)</SectionLabel>
        <div className="grid sm:grid-cols-2 gap-5">
          {FXs.map(([k, l]) => <FNum key={k} id={k} label={l} value={fx[k]} onChange={(v) => setFX(k, v)}/>)}
        </div>
      </div>
    </div>
  );
}

function StepLoans({ profile, setProfile }) {
  const add = () => setProfile((p) => ({ ...p, liabilities_table: [...(p.liabilities_table||[]), { _id: `l-${Date.now()}-${Math.random().toString(36).slice(2,7)}`, type: "Housing Loan", lender: "", start_year: "", end_year: "", monthly_emi: "", interest_rate: "", outstanding: "" }] }));
  const upd = (i, k, v) => setProfile((p) => {
    const arr = [...(p.liabilities_table||[])]; arr[i] = { ...arr[i], [k]: v };
    return { ...p, liabilities_table: arr };
  });
  const rm = (i) => setProfile((p) => {
    const arr = [...(p.liabilities_table||[])]; arr.splice(i, 1);
    return { ...p, liabilities_table: arr };
  });
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl sm:text-3xl font-medium">Loans & liabilities</h2>
        <p className="text-[#5B5E5B] mt-2">Add each loan you currently service.</p>
      </div>
      <div className="flex justify-end">
        <Button type="button" data-testid="add-loan" onClick={add} variant="ghost" className="rounded-full text-[#1A3626]">
          <Plus className="h-4 w-4 mr-1"/> Add loan
        </Button>
      </div>
      {(profile.liabilities_table || []).length === 0 && <p className="text-sm text-[#8A8D8A]">No loans added.</p>}
      <div className="space-y-4">
        {(profile.liabilities_table || []).map((l, i) => (
          <div key={l._id || `loan-${i}`} className="rounded-xl border border-[#E5E2DA] p-5 bg-[#FDFBF7]">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-medium">Loan {i + 1}</div>
              <Button type="button" variant="ghost" data-testid={`rm-loan-${i}`} onClick={() => rm(i)} className="text-[#9E473D] hover:text-[#9E473D]">
                <Trash2 className="h-4 w-4"/>
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="block text-sm font-medium text-[#1C1D1C] mb-2">Loan type</Label>
                <Select value={l.type || "Housing Loan"} onValueChange={(v) => upd(i, "type", v)}>
                  <SelectTrigger data-testid={`loan-${i}-type`} className={inputCls}><SelectValue/></SelectTrigger>
                  <SelectContent>{LOAN_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <FText id={`loan_${i}_lender`} label="Lender's name" value={l.lender} onChange={(v) => upd(i, "lender", v)}/>
              <FText id={`loan_${i}_start`} label="Start year" type="number" value={l.start_year} onChange={(v) => upd(i, "start_year", v)}/>
              <FText id={`loan_${i}_end`} label="End year" type="number" value={l.end_year} onChange={(v) => upd(i, "end_year", v)}/>
              <FNum id={`loan_${i}_emi`} label="Monthly EMI" value={l.monthly_emi} onChange={(v) => upd(i, "monthly_emi", v)}/>
              <FNum id={`loan_${i}_int`} label="Interest rate" prefix="" suffix="%" value={l.interest_rate} onChange={(v) => upd(i, "interest_rate", v)}/>
              <div className="sm:col-span-2">
                <FNum id={`loan_${i}_out`} label="Outstanding amount" value={l.outstanding} onChange={(v) => upd(i, "outstanding", v)}/>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepInvestments({ profile, setSection }) {
  const im = profile.investments_monthly, iy = profile.investments_yearly;
  const setIM = (k, v) => setSection("investments_monthly", { [k]: v });
  const setIY = (k, v) => setSection("investments_yearly", { [k]: v });
  const M = [
    ["sip_mutual_funds", "Mutual Fund SIPs"], ["recurring_deposit", "Recurring Deposit"],
    ["ppf", "PPF (monthly)"], ["epf", "EPF (monthly)"], ["nps", "NPS"],
    ["endowment_moneyback", "Endowment / Moneyback premium"], ["ulip", "ULIP premium"],
    ["gold_scheme", "Gold Savings Scheme"], ["pomis_nsc_kvp", "POMIS / NSC / KVP"],
    ["esops_espp", "ESOPs / ESPP contribution"], ["sukanya_samriddhi", "Sukanya Samriddhi"],
    ["other", "Other monthly"],
  ];
  const Y = [
    ["ppf", "PPF (annual top-up)"], ["nps", "NPS (annual)"],
    ["insurance_premium", "Insurance premium (annual)"], ["sukanya_samriddhi", "Sukanya Samriddhi (annual)"],
    ["elss", "ELSS / tax-saver"], ["other", "Other yearly"],
  ];
  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-heading text-2xl sm:text-3xl font-medium">Investments</h2>
        <p className="text-[#5B5E5B] mt-2">What you put away every month — and any annual lump-sum contributions.</p>
      </div>
      <div>
        <SectionLabel>Monthly investments</SectionLabel>
        <div className="grid sm:grid-cols-2 gap-5">
          {M.map(([k, l]) => <FNum key={k} id={`im_${k}`} label={l} value={im[k]} onChange={(v) => setIM(k, v)}/>)}
        </div>
      </div>
      <div>
        <SectionLabel>Yearly investments</SectionLabel>
        <div className="grid sm:grid-cols-2 gap-5">
          {Y.map(([k, l]) => <FNum key={k} id={`iy_${k}`} label={l} value={iy[k]} onChange={(v) => setIY(k, v)}/>)}
        </div>
      </div>
    </div>
  );
}

function StepInsurance({ profile, setSection }) {
  const ins = profile.insurance;
  const set = (k, v) => setSection("insurance", { [k]: v });
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl sm:text-3xl font-medium">Insurance</h2>
        <p className="text-[#5B5E5B] mt-2">Your safety nets — for life and health.</p>
      </div>
      <div className="rounded-xl border border-[#E5E2DA] p-5 bg-[#FDFBF7]">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium">Term life insurance</div>
            <div className="text-sm text-[#8A8D8A]">A pure protection policy in your name</div>
          </div>
          <Switch data-testid="field-has_term_insurance" checked={!!ins.has_term_insurance} onCheckedChange={(v) => set("has_term_insurance", v)}/>
        </div>
        {ins.has_term_insurance && (
          <div className="grid sm:grid-cols-2 gap-5 mt-5">
            <FNum id="term_cover_amount" label="Sum assured" value={ins.term_cover_amount} onChange={(v) => set("term_cover_amount", v)}/>
            <FNum id="term_premium" label="Annual premium" value={ins.term_premium} onChange={(v) => set("term_premium", v)}/>
          </div>
        )}
      </div>
      <div className="rounded-xl border border-[#E5E2DA] p-5 bg-[#FDFBF7]">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium">Health insurance (personal / floater)</div>
            <div className="text-sm text-[#8A8D8A]">Excluding employer cover</div>
          </div>
          <Switch data-testid="field-has_health_insurance" checked={!!ins.has_health_insurance} onCheckedChange={(v) => set("has_health_insurance", v)}/>
        </div>
        {ins.has_health_insurance && (
          <div className="grid sm:grid-cols-2 gap-5 mt-5">
            <FNum id="health_cover_amount" label="Cover amount" value={ins.health_cover_amount} onChange={(v) => set("health_cover_amount", v)}/>
            <FNum id="health_premium" label="Annual premium" value={ins.health_premium} onChange={(v) => set("health_premium", v)}/>
          </div>
        )}
      </div>
      <div className="rounded-xl border border-[#E5E2DA] p-5 bg-[#FDFBF7]">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium">Employer health cover</div>
            <div className="text-sm text-[#8A8D8A]">Group cover from your company</div>
          </div>
          <Switch data-testid="field-has_employer_health" checked={!!ins.has_employer_health} onCheckedChange={(v) => set("has_employer_health", v)}/>
        </div>
        {ins.has_employer_health && (
          <div className="mt-5">
            <FNum id="employer_health_cover" label="Cover amount" value={ins.employer_health_cover} onChange={(v) => set("employer_health_cover", v)}/>
          </div>
        )}
      </div>
    </div>
  );
}

function StepGoals({ profile, setProfile }) {
  const add = () => setProfile((p) => ({ ...p, goals: [...(p.goals||[]), { _id: `g-${Date.now()}-${Math.random().toString(36).slice(2,7)}`, name: "Retirement", today_cost: "", target_year: "", inflation: "7", comment: "" }] }));
  const upd = (i, k, v) => setProfile((p) => {
    const arr = [...(p.goals||[])]; arr[i] = { ...arr[i], [k]: v };
    return { ...p, goals: arr };
  });
  const rm = (i) => setProfile((p) => {
    const arr = [...(p.goals||[])]; arr.splice(i, 1);
    return { ...p, goals: arr };
  });
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl sm:text-3xl font-medium">Financial goals</h2>
        <p className="text-[#5B5E5B] mt-2">What you&apos;re planning for — at today&apos;s cost. Your advisor inflates it forward.</p>
      </div>
      <div className="flex justify-end">
        <Button type="button" data-testid="add-goal" onClick={add} variant="ghost" className="rounded-full text-[#1A3626]">
          <Plus className="h-4 w-4 mr-1"/> Add goal
        </Button>
      </div>
      {(profile.goals || []).length === 0 && <p className="text-sm text-[#8A8D8A]">No goals added.</p>}
      <div className="space-y-4">
        {(profile.goals || []).map((g, i) => (
          <div key={g._id || `goal-${i}`} className="rounded-xl border border-[#E5E2DA] p-5 bg-[#FDFBF7]">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-medium">Goal {i + 1}</div>
              <Button type="button" variant="ghost" data-testid={`rm-goal-${i}`} onClick={() => rm(i)} className="text-[#9E473D] hover:text-[#9E473D]">
                <Trash2 className="h-4 w-4"/>
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="block text-sm font-medium text-[#1C1D1C] mb-2">Goal</Label>
                <Select value={g.name || "Retirement"} onValueChange={(v) => upd(i, "name", v)}>
                  <SelectTrigger data-testid={`goal-${i}-name`} className={inputCls}><SelectValue/></SelectTrigger>
                  <SelectContent>{GOAL_PRESETS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <FNum id={`goal_${i}_cost`} label="Today's cost" value={g.today_cost} onChange={(v) => upd(i, "today_cost", v)}/>
              <FText id={`goal_${i}_year`} label="Target year" type="number" value={g.target_year} onChange={(v) => upd(i, "target_year", v)}/>
              <FNum id={`goal_${i}_infl`} label="Inflation assumption" prefix="" suffix="%" value={g.inflation} onChange={(v) => upd(i, "inflation", v)}/>
              <div className="sm:col-span-2">
                <Label className="block text-sm font-medium text-[#1C1D1C] mb-2">Comment</Label>
                <Textarea data-testid={`goal-${i}-comment`} value={g.comment || ""} onChange={(e) => upd(i, "comment", e.target.value)} placeholder="Any context for your advisor…"
                  className="rounded-xl border-[#E5E2DA] bg-white"/>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="pt-2">
        <Label className="block text-sm font-medium text-[#1C1D1C] mb-2">Anything else your advisor should know?</Label>
        <Textarea data-testid="field-financial_notes" value={profile.financial_notes || ""}
          onChange={(e) => setProfile((p) => ({ ...p, financial_notes: e.target.value }))}
          placeholder="Other priorities, concerns, expected lump-sums…"
          className="min-h-[120px] rounded-xl border-[#E5E2DA] bg-white"/>
      </div>
    </div>
  );
}

function LockedView({ user, profile, navigate, logout, onUnlock, reopening }) {
  const updatedAt = profile?.updated_at ? new Date(profile.updated_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "—";
  return (
    <div className="min-h-screen page-tinted flex items-center justify-center px-6 relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-[#F4B860] opacity-[0.2] blur-[110px]"/>
      <div aria-hidden className="pointer-events-none absolute -bottom-32 -left-32 h-[420px] w-[420px] rounded-full bg-[#A7C7E7] opacity-[0.22] blur-[110px]"/>
      <div className="max-w-2xl w-full relative">
        <div className="rounded-3xl border border-[#E5E2DA] bg-white/95 backdrop-blur-xl p-10 md:p-14 fade-up">
          <div className="flex items-start gap-4 mb-6">
            <div className="h-12 w-12 rounded-full bg-[#1A3626]/10 flex items-center justify-center">
              <Lock className="h-6 w-6 text-[#1A3626]" strokeWidth={1.5}/>
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Intake locked</span>
              <h1 className="font-heading text-3xl font-medium mt-1">You&apos;ve submitted, {user?.name ? user.name.split(" ")[0] : "champ"}.</h1>
              <p className="text-[#5B5E5B] mt-3 leading-relaxed">
                Your details from <strong>{updatedAt}</strong> are with your Wealth Accelerator Hub advisor. The form is locked to keep the picture consistent.
              </p>
              <p className="text-sm text-[#8A8D8A] mt-3">
                Something changed since then? Use the button below to unlock and update your details for this month.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Button data-testid="reopen-intake" onClick={onUnlock} disabled={reopening}
              className="rounded-full bg-[#1A3626] hover:bg-[#2C4C3B] text-white h-12 px-7">
              <Sparkles className="h-4 w-4 mr-2"/>
              {reopening ? "Unlocking…" : "Update my details"}
            </Button>
            <Button data-testid="goto-dashboard" variant="outline" onClick={() => navigate("/dashboard")} className="rounded-full border-[#E5E2DA] h-12 px-7">
              Back to dashboard
            </Button>
            <Button data-testid="locked-logout" variant="ghost" onClick={async () => { await logout(); navigate("/"); }} className="rounded-full ml-auto">
              <LogOut className="h-4 w-4 mr-2"/> Sign out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SubmissionSuccess({ user, navigate, logout }) {
  const WEALTHY_URL = "https://wealthy.in/partners/sande92406/?lang=en";
  return (
    <div className="min-h-screen page-tinted flex items-center justify-center px-6 relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-[#F4B860] opacity-[0.25] blur-[110px]"/>
      <div aria-hidden className="pointer-events-none absolute -bottom-32 -left-32 h-[420px] w-[420px] rounded-full bg-[#A7C7E7] opacity-[0.25] blur-[110px]"/>
      <div className="max-w-2xl w-full relative">
        <div className="rounded-3xl border border-[#E5E2DA] bg-white/95 backdrop-blur-xl p-10 md:p-14 text-center fade-up">
          <div className="mx-auto w-16 h-16 rounded-full bg-[#3A5C45]/10 flex items-center justify-center mb-6">
            <PartyPopper className="h-8 w-8 text-[#1A3626]" strokeWidth={1.5}/>
          </div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Submission received</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-medium mt-3 tracking-tight">
            Thank you{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
          </h1>
          <p className="text-[#5B5E5B] mt-4 leading-relaxed">
            Your financial datasheet is now with your Wealth Accelerator Hub advisor.
            We&apos;ll review your numbers and reach out shortly with a personalised plan tailored to your goals.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Button data-testid="start-investment-journey" asChild
              className="rounded-full bg-[#1A3626] hover:bg-[#2C4C3B] text-white h-12 px-8 hover:-translate-y-0.5 transition-all">
              <a href={WEALTHY_URL} target="_blank" rel="noopener noreferrer">
                Start Your Investment Journey <ArrowRight className="h-4 w-4 ml-2"/>
              </a>
            </Button>
            <Button data-testid="back-to-dashboard-success" variant="outline" onClick={() => navigate("/dashboard")} className="rounded-full border-[#E5E2DA] h-12 px-8">
              Go to dashboard
            </Button>
          </div>
          <button data-testid="success-logout" onClick={async () => { await logout(); navigate("/"); }} className="text-sm text-[#8A8D8A] hover:text-[#1C1D1C] mt-8 inline-flex items-center">
            <LogOut className="h-3.5 w-3.5 mr-1.5"/> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
